import {Box, Grid, Heading, Link, River, RiverBreakout, SectionIntro, Text, Timeline} from '@primer/react-brand'

import {analyticsEvent} from '../../../../lib/analytics'
import {Image} from '../../../../components/Image/Image'

import CopilotExtensionsImage from '../_assets/copilot-extensions.webp'

import FeaturesBreakoutAgentModeImage from '../_assets/features-breakout-agent-mode.webp'
import FeaturesBreakoutAgentModeVideo from '../_assets/features-breakout-agent-mode.mp4'

import FeaturesCodeReviewImage from '../_assets/features-code-review.webp'
import FeaturesModelsImage from '../_assets/features-models.webp'
import FeaturesNextEditSuggestionsImage from '../_assets/features-next-edit-suggestions.webp'

import AutoPlayVideo from './AutoPlayVideo'

export default function FeaturesSection() {
  return (
    <section
      id="features"
      className="lp-Section lp-Section--compact lp-SectionIntro--compact"
      style={{
        position: 'relative',
        zIndex: 1,
        background: 'rgb(13, 17, 23) linear-gradient(180deg, #161B22 0%, #0D1117 25%)',
        paddingTop: 'var(--base-size-96)',
      }}
    >
      <Grid className="lp-Section-container--centerUntilMedium lp-Grid--noRowGap">
        <Grid.Column span={12}>
          <SectionIntro fullWidth align="center" className="lp-SectionIntro">
            <SectionIntro.Label className="lp-Label--section">Features</SectionIntro.Label>

            <SectionIntro.Heading size="2" weight="semibold">
              Agent mode: Because two brains are better than one
            </SectionIntro.Heading>
          </SectionIntro>
        </Grid.Column>

        <Grid.Column span={12}>
          <RiverBreakout className="lp-RiverBreakout">
            <RiverBreakout.Visual className="lp-River-visual">
              <AutoPlayVideo
                src={FeaturesBreakoutAgentModeVideo}
                poster={FeaturesBreakoutAgentModeImage}
                width="1248"
                height="647"
                className="d-none d-md-block lp-RiverBreakout-video--lg"
                aria-label="A demonstration animation of a code editor using GitHub Copilot’s agent mode, where the user requests GitHub Copilot to update their website project to allow searching for running races by name."
                darkButton
                analyticsProps={{
                  context: 'demo_gif',
                  location: 'river_breakout',
                }}
              />
              <AutoPlayVideo
                src={FeaturesBreakoutAgentModeVideo}
                poster={FeaturesBreakoutAgentModeImage}
                width="350"
                height="380"
                className="d-block d-md-none lp-RiverBreakout-video--sm"
                aria-label="A demonstration animation of a code editor using GitHub Copilot’s agent mode, where the user requests GitHub Copilot to update their website project to allow searching for running races by name."
                darkButton
                analyticsProps={{
                  context: 'demo_gif',
                  location: 'river_breakout',
                }}
              />
            </RiverBreakout.Visual>

            <RiverBreakout.Content
              trailingComponent={() => (
                <Timeline className="lp-Timeline">
                  <Timeline.Item>
                    <em>Plans the path, handles the work.</em> Agent mode reasons through the problem, coordinates next
                    steps, and applies the changes—while keeping you in the driver’s seat.
                  </Timeline.Item>
                  <Timeline.Item>
                    <em>Don’t sweat the small stuff.</em> From renaming variables to fixing errors, agent mode manages
                    the tedious tasks that might otherwise break your flow.
                  </Timeline.Item>
                </Timeline>
              )}
            >
              <Text style={{maxWidth: 690}}>
                <em>From broken to brilliant, fast.</em> Need to make a sweeping change? Agent mode helps you do it
                quickly by analyzing code, proposing edits, running tests, and validating results across multiple files.
                <span style={{marginTop: '32px', marginBottom: '48px', display: 'block'}}>
                  <Link
                    href="https://aka.ms/vscode-copilot-agent"
                    variant="accent"
                    {...analyticsEvent({
                      action: 'try_agent_mode',
                      tag: 'link',
                      context: 'copilot_agent_mode',
                      location: 'features',
                    })}
                  >
                    Try agent mode
                  </Link>
                </span>
              </Text>
            </RiverBreakout.Content>
          </RiverBreakout>

          <Box paddingBlockStart={48} aria-hidden />

          <River imageTextRatio="50:50" className="lp-River-mod">
            <River.Visual className="lp-River-visual">
              <Image
                src={FeaturesModelsImage}
                alt="A dropdown menu lists some of GitHub Copilot’s available models like Claude 3.5 Sonnet, Claude 3.7 Sonnet, Gemini 2.0 Flash, GPT-4o, o1 (Preview) and 03-mini."
                width="708"
                height="472"
              />
            </River.Visual>

            <River.Content>
              <Heading as="h3" size="5">
                Pick your brainpower
              </Heading>

              <Text as="p" variant="muted" className="lp-River-text">
                Swap between models like{' '}
                <a
                  className="lp-Link--inline"
                  href="https://docs.github.com/en/copilot/using-github-copilot/using-claude-sonnet-in-github-copilot"
                  {...analyticsEvent({
                    action: 'docs_instructions',
                    tag: 'inline_link',
                    context: 'using_sonnet',
                    location: 'features',
                  })}
                >
                  Claude 3.7 Sonnet
                </a>
                , OpenAI o1, and Google Gemini 2.0 Flash to crush coding tasks fast or go deep when it counts.
              </Text>
            </River.Content>
          </River>

          <River imageTextRatio="50:50" className="lp-River-mod">
            <River.Visual className="lp-River-visual">
              <Image
                src={FeaturesNextEditSuggestionsImage}
                alt="Active popover above a checkmark button indicator in front of a line of code with the options: Go To/Accept (tab key), Reject (escape key), Show Collapsed, and Settings."
                width="668"
                height="442"
              />
            </River.Visual>

            <River.Content>
              <Heading as="h3" size="5">
                Drop an edit. <br className="break-whenWide" />
                Watch it spread
              </Heading>

              <Text as="p" variant="muted" className="lp-River-text">
                Next edit suggestions reveal the ripple effects of your changes across your project—helping you keep
                everything consistent.
              </Text>

              <Link
                href="https://code.visualstudio.com/docs/copilot/ai-powered-suggestions#_next-edit-suggestions"
                variant="accent"
                {...analyticsEvent({
                  action: 'docs_instructions',
                  tag: 'link',
                  context: 'next_edit_suggestions',
                  location: 'features',
                })}
              >
                Try next edit suggestions
              </Link>
            </River.Content>
          </River>

          <River imageTextRatio="50:50" className="lp-River-mod">
            <River.Visual className="lp-River-visual">
              <a
                href="https://docs.github.com/en/copilot/using-github-copilot/code-review/using-copilot-code-review"
                tabIndex={-1}
                {...analyticsEvent({
                  action: 'docs_instructions',
                  tag: 'image',
                  context: 'copilot_review',
                  location: 'features',
                })}
              >
                <Image
                  src={FeaturesCodeReviewImage}
                  alt="GitHub Copilot performing a code review. In an active popover where reviewers can be requested, Copilot is selected."
                  width="708"
                  height="472"
                />
              </a>
            </River.Visual>

            <River.Content>
              <Heading as="h3" size="5">
                Your code’s guardian angel
              </Heading>

              <Text as="p" variant="muted" className="lp-River-text">
                Code review analyzes your work, uncovers hidden bugs, fixes mistakes, and more—before a human ever sees
                it.
              </Text>

              <Link
                href="https://docs.github.com/en/copilot/using-github-copilot/code-review/using-copilot-code-review"
                variant="accent"
                {...analyticsEvent({
                  action: 'docs_instructions',
                  tag: 'link',
                  context: 'copilot_review',
                  location: 'features',
                })}
              >
                Discover code review
              </Link>
            </River.Content>
          </River>

          <River imageTextRatio="50:50" className="lp-River-mod">
            <River.Visual className="lp-River-visual">
              <a
                href="https://github.com/features/copilot/extensions"
                tabIndex={-1}
                {...analyticsEvent({
                  action: 'docs_instructions',
                  tag: 'image',
                  context: 'copilot_extensions',
                  location: 'features',
                })}
              >
                <Image
                  src={CopilotExtensionsImage}
                  alt="GitHub Copilot Extensions for Azure, Datastax, Docker, MongoDB and Sentry."
                  width="708"
                  height="472"
                />
              </a>
            </River.Visual>

            <River.Content>
              <Heading as="h3" size="5">
                Everything you need, right where you code
              </Heading>

              <Text as="p" variant="muted" className="lp-River-text">
                Thanks to an ecosystem of third-party extensions, Copilot Chat lets you check logs, toggle features, and
                deploy apps, without ever leaving your editor.
              </Text>

              <Link
                href="https://github.com/features/copilot/extensions"
                variant="accent"
                {...analyticsEvent({
                  action: 'docs_instructions',
                  tag: 'link',
                  context: 'copilot_extensions',
                  location: 'features',
                })}
              >
                Explore extensions
              </Link>
            </River.Content>
          </River>
        </Grid.Column>
      </Grid>
    </section>
  )
}

try{ FeaturesSection.displayName ||= 'FeaturesSection' } catch {}